import React, { useContext } from 'react'
import styled from 'styled-components'
import { marginSizes } from '@jarvis.oddle/core/theme/default'
import { isEmpty } from 'src/utils/string'
import Divider from '@jarvis.oddle/core/components/Divider'
import Div from '@jarvis.oddle/core/components/Div'
import Form from '@jarvis.oddle/core/components/Form'
import { H4 } from '@jarvis.oddle/core/components/Typo'
import { getOrderedAddressFields } from '@jarvis.oddle/core/tools/address'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import {
  CardGroup,
  CardHeader,
  CardLine,
  InputContainer,
  Section
} from './Elements'
import { addressConfig } from './BillingAddress'

interface PickupAddressProps {
  storeName: string
  storeAddress: string
}

const PickupAddress = ({
  storeName = '',
  storeAddress = ''
}: PickupAddressProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  return (
    <CardGroup>
      <CardLine heading={t('Store Name')} text={storeName} />
      <CardLine heading={t('Store Address')} text={storeAddress} />
    </CardGroup>
  )
}

interface DeliveryAddressProps {
  addressFormat: string
  addressRequiredFields: string
  allowEdit: boolean
}

const DeliveryAddress = ({
  addressFormat,
  addressRequiredFields,
  allowEdit = false
}: DeliveryAddressProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const orderedAddressFields = getOrderedAddressFields(
    addressFormat,
    addressRequiredFields
  )
  return (
    <React.Fragment>
      {(orderedAddressFields || [])
        .reduce((row, item, addressIdx) => {
          if (addressIdx % 2 === 0) row.push([])
          row[row.length - 1].push(item)
          return row
        }, [])
        .map((rowContent, rowIdx) => (
          <CardGroup key={rowIdx}>
            {rowContent.map((content, contentIdx) => {
              const config = addressConfig[content.field](t)
              const label = config.heading
              const name = config.deliveryFieldName
              return (
                <InputContainer key={contentIdx}>
                  <Form.Input
                    type='text'
                    name={name}
                    label={label}
                    disabled={
                      (allowEdit && content.field !== 'addressTwo') ||
                      !allowEdit
                    }
                  />
                </InputContainer>
              )
            })}
          </CardGroup>
        ))}
    </React.Fragment>
  )
}

const FormTextArea = styled(Form.TextArea)`
  height: 80px;
  resize: none;
  border: none;
`

interface SimplifiedDeliveryAddressProps {
  deliveryAddress: string
  allowEdit: boolean
}

const SimplifiedDeliveryAddress = ({
  deliveryAddress = '',
  allowEdit = false
}: SimplifiedDeliveryAddressProps) => {
  const { t } = useContext(TranslationContext)

  return allowEdit ? (
    <React.Fragment>
      <Div marginBottom={marginSizes.small}>
        <FormTextArea
          name='deliveryAddressAutoComplete'
          label={t('Address')}
          disabled
        />
      </Div>
      <Div>
        <Form.Input
          type='text'
          name='deliveryAddressTwo'
          label={t('Building Name or Unit Number')}
        />
      </Div>
    </React.Fragment>
  ) : (
    <H4 normalised>{deliveryAddress}</H4>
  )
}

interface AppointmentProps {
  storeOrder: {
    data: Oms.V4.StoreOrder
  }
  addressFormat: string
  addressRequiredFields: string
  allowEdit: boolean
}

const Appointment = ({
  storeOrder = {} as {
    data: Oms.V4.StoreOrder
  },
  addressFormat,
  addressRequiredFields,
  allowEdit = false
}: AppointmentProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const isPickup = storeOrder.data.isPickup
  return (
    <React.Fragment>
      <Section>
        <CardHeader>
          {isPickup ? t('Pickup Store Address') : t('Delivery Address')}
        </CardHeader>
        {isPickup ? (
          <PickupAddress
            storeName={storeOrder.data.storeName}
            storeAddress={storeOrder.data.storeAddress}
          />
        ) : isEmpty(storeOrder.data.deliveryAddressAutoComplete) ? (
          <DeliveryAddress
            allowEdit={allowEdit}
            addressFormat={addressFormat}
            addressRequiredFields={addressRequiredFields}
          />
        ) : (
          <SimplifiedDeliveryAddress
            allowEdit={allowEdit}
            deliveryAddress={storeOrder.data.deliveryAddress}
          />
        )}
      </Section>
      <Divider />
    </React.Fragment>
  )
}

export default Appointment
